import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { api, ErroreApi } from '../../servizi/api.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Etichetta, Modale, Riquadro, Scelta, Spunta, Tabella } from '../../componenti/base.tsx'
import { useLingue } from '../../componenti/persona-campi.tsx'
import { codiceDaVoce, opzioniConAltreVoci } from '../../servizi/lingue.ts'
import { SegnaNonPresentata, SegnaSvolta } from '../mediazioni/comandi.tsx'
import {
  DURATE, STATI, doveLeggibile, durataLeggibile, fraseEmail, giornoDiOggi, giornoVicino,
  type EsitoEmail, type EsitoSvolta, type Mediatore, type MediazioneScheda, type MediazioniPersona,
  type PercorsoMediazione,
} from '../../servizi/mediazioni.ts'
import { data as dataBreve } from './comune.ts'
import { opzioniSedi } from '../../servizi/sedi.ts'
import { SceltaEnte, type Ente } from '../Registro.tsx'
import { SceltaStanza } from '../../componenti/stanze.tsx'

/**
 * Mediazioni della persona (indicazioni CIDIS del 17/10/2026; esito, 457, decisioni del 18/10/2026):
 * l'operatore programma una mediazione scegliendo il mediatore dall'albo e il percorso su cui
 * finiranno le ore, e il mediatore la trova fra le sue con giorno e ora. Da qui si sposta, si
 * annulla, si segna che la persona non si è presentata e, per chi non usa il gestionale, si segna
 * come svolta. Dalla mediazione svolta nasce l'intervento in bozza da confermare; se non è potuto
 * nascere, da qui si registra.
 *
 * Dal 2/11/2026 (532) la tendina dei mediatori dice il comune in cui lavorano, e a mediazione programmata
 * chi programma legge il «Recapito del mediatore», se c'è: dove riceve, da dire alla persona. Non è il
 * luogo della mediazione, che resta nella colonna «Dove».
 */

type Finestra =
  | { tipo: 'nuova' }
  | { tipo: 'modifica' | 'annulla' | 'svolta' | 'non_presentata' | 'intervento'; mediazione: MediazioneScheda }
type Esito = { testo: string; tono: 'successo' | 'attenzione' }

const nomeMediatore = (m: { mediatore_nome: string; mediatore_cognome: string }) =>
  `${m.mediatore_nome} ${m.mediatore_cognome}`

/** Dopo la svolta: dove sono finite le ore, o perché l'intervento manca. */
function esitoSvolta(r: EsitoSvolta): Esito {
  if (r.intervento) {
    return {
      tono: 'successo',
      testo: 'Mediazione segnata come svolta. Le ore sono in un intervento in bozza sul percorso di mediazione: '
        + 'aprilo dalla linguetta Percorsi, completa quello che manca e salvalo.',
    }
  }
  return {
    tono: 'attenzione',
    testo: 'Mediazione segnata come svolta, ma l’intervento non si è potuto registrare. '
      + `${r.intervento_mancato?.messaggio ?? ''} Quando è risolto, usa «Registra l’intervento» nella riga della mediazione.`,
  }
}

export function SezioneMediazioni({ personaId, nomePersona, sedePersona, ricaricaScheda }: {
  personaId: string; nomePersona: string; sedePersona: string | null
  /** Rilegge la scheda: serve quando da qui si apre un percorso, che compare nella linguetta Percorsi. */
  ricaricaScheda?: () => Promise<void> | void
}) {
  const [dati, setDati] = useState<MediazioniPersona | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [finestra, setFinestra] = useState<Finestra | null>(null)
  const [esito, setEsito] = useState<Esito | null>(null)

  const carica = useCallback(async () => {
    try {
      setDati(await api.get<MediazioniPersona>(`/persone/${personaId}/mediazioni`))
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non riesco a caricare le mediazioni.')
    }
  }, [personaId])
  useEffect(() => { void carica() }, [carica])

  const chiudi = () => setFinestra(null)
  const fatto = (testo: string, tono: Esito['tono'] = 'successo') => {
    setFinestra(null); setEsito({ testo, tono }); void carica()
  }
  const apri = (f: Finestra) => { setEsito(null); setFinestra(f) }
  /** Dopo l'apertura di un percorso: si rileggono le mediazioni e la scheda. */
  const percorsoAperto = async () => { await carica(); await ricaricaScheda?.() }

  if (errore) return <Avviso tipo="errore">{errore}</Avviso>
  if (!dati) return <Attesa testo="Caricamento delle mediazioni…" />

  const oggi = giornoDiOggi()
  return (
    <Riquadro denso titolo="Mediazioni" azioni={dati.puo_programmare
      ? <Bottone variante="primario" dimensione="piccolo" onClick={() => apri({ tipo: 'nuova' })}>
          Programma una mediazione
        </Bottone>
      : undefined}>
      {esito && <Avviso tipo={esito.tono}>{esito.testo}</Avviso>}
      <Tabella intestazioni={['Quando', 'Mediatore', 'Dove', 'Stato', '']}
               vuoto={<Vuoto testo={`Nessuna mediazione per ${nomePersona}.`}
                             azione={dati.puo_programmare
                               ? <Bottone variante="secondario" onClick={() => apri({ tipo: 'nuova' })}>
                                   Programma la prima
                                 </Bottone>
                               : undefined} />}>
        {dati.mediazioni.map((m) => (
          <tr key={m.id}>
            <td data-etichetta="Quando">
              <div className="voce-principale">{giornoVicino(m.data)} alle {m.ora}</div>
              <div className="piccolo tenue">{durataLeggibile(m.durata_minuti)}{m.lingua_nome && ` · ${m.lingua_nome}`}</div>
            </td>
            <td data-etichetta="Mediatore">
              <div>{nomeMediatore(m)}</div>
              {m.mediatore_recapito && (
                <div className="piccolo">Recapito del mediatore: {m.mediatore_recapito}</div>
              )}
              {!m.mediatore_usa_gestionale && m.stato === 'programmata' && (
                <div className="piccolo tenue">Non usa il gestionale: va avvisato a voce</div>
              )}
            </td>
            <td data-etichetta="Dove">
              <div>{doveLeggibile(m)}{m.risorsa_nome && ` · ${m.risorsa_nome}`}</div>
              {m.ente_nome && <div className="piccolo">Per: {m.ente_sigla ? `${m.ente_sigla} — ${m.ente_nome}` : m.ente_nome}</div>}
              {m.serie_id && m.serie_quante > 1 && <div className="piccolo tenue">Una di {m.serie_quante} mediazioni ripetute</div>}
              {m.nota && <div className="piccolo tenue">Nota: {m.nota}</div>}
            </td>
            <td data-etichetta="Stato">
              <Etichetta tono={STATI[m.stato].tono}>{STATI[m.stato].etichetta}</Etichetta>
              {m.stato === 'svolta' && m.ore_svolte != null && (
                <div className="piccolo tenue">{durataLeggibile(m.ore_svolte * 60)}</div>
              )}
              {m.stato === 'svolta' && <StatoIntervento m={m} />}
              {m.stato === 'annullata' && m.motivo_annullamento && (
                <div className="piccolo tenue">{m.motivo_annullamento}</div>
              )}
            </td>
            <td data-etichetta="">
              {dati.puo_programmare && m.stato === 'programmata' && (
                <div className="riga riga-a-destra">
                  {/* Il file per il calendario (557): lingua, sede e orari, nessun nome. */}
                  <a className="bottone bottone-discreto bottone-piccolo" href={`/api/mediazioni/${m.id}.ics`} download
                     title="Il file per il calendario del telefono o di Outlook, senza nomi">.ics</a>
                  <Bottone variante="discreto" dimensione="piccolo" onClick={() => apri({ tipo: 'modifica', mediazione: m })}>
                    Sposta
                  </Bottone>
                  <Bottone variante="discreto" dimensione="piccolo" onClick={() => apri({ tipo: 'annulla', mediazione: m })}>
                    Annulla
                  </Bottone>
                  {m.data <= oggi && (
                    <Bottone variante="discreto" dimensione="piccolo"
                             onClick={() => apri({ tipo: 'non_presentata', mediazione: m })}>
                      Non si è presentata
                    </Bottone>
                  )}
                  {!m.mediatore_usa_gestionale && m.data <= oggi && (
                    <Bottone variante="secondario" dimensione="piccolo" onClick={() => apri({ tipo: 'svolta', mediazione: m })}>
                      Segna come svolta
                    </Bottone>
                  )}
                </div>
              )}
              {dati.puo_registrare_intervento && m.stato === 'svolta' && !m.intervento_id && (
                <div className="riga riga-a-destra">
                  <Bottone variante="secondario" dimensione="piccolo" onClick={() => apri({ tipo: 'intervento', mediazione: m })}>
                    Registra l’intervento
                  </Bottone>
                </div>
              )}
            </td>
          </tr>
        ))}
      </Tabella>

      {(finestra?.tipo === 'nuova' || finestra?.tipo === 'modifica') && (
        <ModuloMediazione personaId={personaId} sedePersona={sedePersona} dati={dati}
                          mediazione={finestra.tipo === 'modifica' ? finestra.mediazione : undefined}
                          percorsoAperto={percorsoAperto} chiudi={chiudi} fatto={fatto} />
      )}
      {finestra?.tipo === 'annulla' && (
        <AnnullaMediazione mediazione={finestra.mediazione} chiudi={chiudi}
                           fatto={(r) => {
                             const quali = r.annullate > 1 ? `${r.annullate} mediazioni annullate` : 'Mediazione annullata'
                             fatto(finestra.mediazione.mediatore_usa_gestionale
                               ? [`${quali}. Il mediatore riceve un avviso${r.annullate > 1 ? ', uno solo per tutte' : ''}.`,
                                 fraseEmail(r, true)].filter(Boolean).join(' ')
                               : `${quali}. Il mediatore non usa il gestionale: avvisalo tu.`)
                           }} />
      )}
      {finestra?.tipo === 'svolta' && (
        <SegnaSvolta mediazione={finestra.mediazione} chiudi={chiudi}
                     fatto={(r) => { const e = esitoSvolta(r); fatto(e.testo, e.tono) }} />
      )}
      {finestra?.tipo === 'non_presentata' && (
        <SegnaNonPresentata mediazione={finestra.mediazione} nome={nomePersona} chiudi={chiudi}
                            fatto={() => fatto('Fatto: la mediazione è chiusa perché la persona non si è presentata. '
                              + 'Se serve, programmane un’altra.')} />
      )}
      {finestra?.tipo === 'intervento' && (
        <RegistraInterventoMediazione personaId={personaId} dati={dati} mediazione={finestra.mediazione}
                                      percorsoAperto={percorsoAperto} chiudi={chiudi}
                                      fatto={() => fatto('Intervento registrato in bozza sul percorso di mediazione: '
                                        + 'aprilo dalla linguetta Percorsi, completa quello che manca e salvalo.')} />
      )}
    </Riquadro>
  )
}

/** Sotto lo stato di una mediazione svolta: che ne è dell'intervento. */
function StatoIntervento({ m }: { m: MediazioneScheda }) {
  if (!m.intervento_id) {
    return <div className="piccolo tenue">Intervento non registrato</div>
  }
  return (
    <div className="piccolo tenue">
      {m.intervento_stato === 'bozza' ? 'Intervento in bozza: da confermare in Percorsi' : 'Intervento registrato'}
    </div>
  )
}

// --- percorso su cui finiscono le ore -------------------------------------------------------------

const etichettaPercorso = (p: PercorsoMediazione) => [
  p.tipologia,
  `aperto il ${dataBreve(p.data_apertura)}`,
  p.sede_nome,
  p.progetto,
].filter(Boolean).join(' · ')

/**
 * Scelta del percorso su cui nasce l'intervento quando la mediazione è svolta. Solo i percorsi di
 * mediazione linguistico-culturale della persona su cui chi programma registra interventi; se non
 * ce ne sono, si spiega perché e, quando si può, si apre il percorso da qui.
 */
function SceltaPercorso({ personaId, dati, valore, cambia, percorsoAperto }: {
  personaId: string
  dati: MediazioniPersona
  valore: string
  cambia: (id: string) => void
  percorsoAperto: () => Promise<void>
}) {
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const utilizzabili = dati.percorsi.filter((p) => p.utilizzabile)
  const nuovo = dati.percorso_nuovo

  async function apriPercorso() {
    if (!nuovo) return
    setErrore(null); setInCorso(true)
    try {
      const { id } = await api.post<{ id: string }>('/percorsi', {
        persona_id: personaId, tipologia_codice: nuovo.tipologia_codice, sede_id: nuovo.sede_id,
      })
      await percorsoAperto()
      cambia(id)
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  if (utilizzabili.length) {
    return (
      <Scelta etichetta="Percorso" obbligatorio valore={valore} cambia={cambia}
              vuota={utilizzabili.length > 1 || !valore ? '— scegli —' : undefined}
              opzioni={utilizzabili.map((p) => ({ valore: p.id, etichetta: etichettaPercorso(p) }))}
              aiuto="Quando la mediazione è svolta, le ore diventano un intervento in bozza su questo percorso, che poi confermi tu." />
    )
  }

  const altrui = dati.percorsi.length > 0
  return (
    <Avviso tipo="attenzione">
      <p>
        {altrui
          ? 'I percorsi di mediazione di questa persona sono seguiti da colleghi di un’altra sede o di un altro progetto: '
            + 'non puoi registrarci le ore.'
          : 'Questa persona non ha un percorso di mediazione linguistico-culturale aperto.'}
        {' '}Serve per programmare la mediazione: quando è svolta, le ore diventano un intervento su quel percorso.
      </p>
      {nuovo?.puo_aprire
        ? (
          <div className="riga">
            <Bottone variante="secondario" dimensione="piccolo" inCorso={inCorso} onClick={() => void apriPercorso()}>
              Apri il percorso di mediazione
            </Bottone>
          </div>
        )
        : <p>Chiedi a un operatore che segue la persona di programmare la mediazione, oppure di aprire il percorso.</p>}
      {errore && <p>{errore}</p>}
    </Avviso>
  )
}

/** Il percorso proposto: quello già scelto, se ancora utilizzabile, altrimenti l'unico disponibile. */
function percorsoIniziale(dati: MediazioniPersona, attuale?: string | null): string {
  const utilizzabili = dati.percorsi.filter((p) => p.utilizzabile)
  if (attuale && utilizzabili.some((p) => p.id === attuale)) return attuale
  return utilizzabili.length === 1 ? utilizzabili[0].id : ''
}

// --- programmazione e spostamento -----------------------------------------------------------------

/**
 * Mediatori dell'albo per la tendina, con la ricerca per nome perche' l'albo migrato supera le duecento
 * voci. Con la lingua, prima chi la parla e poi chi non ha lingue nell'albo (2/11/2026: 88 attivi su 138,
 * e scelta la lingua la tendina restava vuota); con la sede della mediazione, in cima quelli che lavorano
 * in quella sede o in tutte (477).
 */
function useMediatori(lingua: string, cerca: string, sede: string) {
  const [mediatori, setMediatori] = useState<Mediatore[] | null>(null)
  useEffect(() => {
    let vivo = true
    const attesa = setTimeout(() => {
      const parametri = new URLSearchParams({ limite: '200' })
      if (lingua) { parametri.set('lingua', lingua); parametri.set('anche_senza_lingua', '1') }
      if (cerca.trim()) parametri.set('cerca', cerca.trim())
      if (sede) parametri.set('prima_sede', sede)
      api.get<Mediatore[]>(`/mediatori?${parametri}`)
        .then((r) => { if (vivo) setMediatori(r) })
        .catch(() => { if (vivo) setMediatori([]) })
    }, 250)
    return () => { vivo = false; clearTimeout(attesa) }
  }, [lingua, cerca, sede])
  return mediatori
}

/** Valori proposti a una mediazione nuova: la creazione rapida dal calendario (557). */
export type InizialiMediazione = {
  data?: string; ora?: string; sede_id?: string | null; lingua?: string
  /** Il mediatore della riga: resta fra le scelte anche se la ricerca non lo porta. */
  mediatore?: { id: string; nome: string; cognome: string; usa_gestionale: boolean }
}

/** Ripetizioni (557): ogni quanto, e al piu' 26 mediazioni in tutto. */
const FREQUENZE = [
  { valore: '', etichetta: 'Non si ripete' },
  { valore: 'settimanale', etichetta: 'Ogni settimana' },
  { valore: 'quindicinale', etichetta: 'Ogni due settimane' },
  { valore: 'mensile', etichetta: 'Ogni mese, nello stesso giorno' },
]

export function ModuloMediazione({
  personaId, sedePersona, dati, mediazione, iniziali, intestazione, percorsoAperto, chiudi, fatto,
}: {
  personaId: string
  sedePersona: string | null
  dati: MediazioniPersona
  mediazione?: MediazioneScheda
  iniziali?: InizialiMediazione
  /** Sopra il modulo: per chi e', quando il modulo si apre dal calendario. */
  intestazione?: ReactNode
  percorsoAperto: () => Promise<void>
  chiudi: () => void
  fatto: (messaggio: string) => void
}) {
  const { sedi } = useSessione()
  const lingue = useLingue()
  const [lingua, setLingua] = useState(mediazione?.lingua ?? iniziali?.lingua ?? '')
  const [cerca, setCerca] = useState('')
  const [mediatoreId, setMediatoreId] = useState(mediazione?.mediatore_id ?? iniziali?.mediatore?.id ?? '')
  const [data, setData] = useState(mediazione?.data ?? iniziali?.data ?? '')
  const [ora, setOra] = useState(mediazione?.ora ?? iniziali?.ora ?? '')
  const [durata, setDurata] = useState(String(mediazione?.durata_minuti ?? 60))
  const [sede, setSede] = useState(mediazione ? mediazione.sede_id ?? ''
    : iniziali?.sede_id !== undefined ? iniziali.sede_id ?? '' : sedePersona ?? '')
  // La stanza (558) e' della sede: cambiando sede si toglie.
  const [stanza, setStanza] = useState(mediazione?.risorsa_id ?? '')
  const [luogo, setLuogo] = useState(mediazione?.luogo ?? '')
  const [nota, setNota] = useState(mediazione?.nota ?? '')
  // Ente (557, decisione 7) e ripetizioni (decisione 10, solo per le mediazioni nuove).
  const [ente, setEnte] = useState<Ente | null>(mediazione?.ente_id
    ? { id: mediazione.ente_id, nome: mediazione.ente_nome ?? '', sigla: mediazione.ente_sigla } : null)
  const [frequenza, setFrequenza] = useState('')
  const [volte, setVolte] = useState('4')
  const [percorsoId, setPercorsoId] = useState(() => percorsoIniziale(dati, mediazione?.percorso_id))
  const [errore, setErrore] = useState<string | null>(null)
  // Avviso da confermare: mediatore gia' impegnato (SOVRAPPOSTA), sede chiusa (FUORI_ORARIO, 556) o mediatore
  // che si e' segnato non disponibile (MEDIATORE_NON_DISPONIBILE, 559). Le conferme date restano: dopo
  // «Programma lo stesso» puo' arrivare un altro avviso. La stanza occupata non si conferma: blocca.
  type CodiceAvviso = 'SOVRAPPOSTA' | 'FUORI_ORARIO' | 'MEDIATORE_NON_DISPONIBILE'
  const [avviso, setAvviso] = useState<{ codice: CodiceAvviso; messaggio: string } | null>(null)
  const [conferme, setConferme] = useState({ sovrapposizione: false, fuori_orario: false, non_disponibile: false })
  const [inCorso, setInCorso] = useState(false)

  const trovati = useMediatori(lingua, cerca, sede)
  // Spostando, il mediatore attuale resta fra le scelte anche se il filtro lo escluderebbe.
  const elenco = [...(trovati ?? [])]
  if (mediazione && !elenco.some((m) => m.id === mediazione.mediatore_id)) {
    elenco.unshift({
      id: mediazione.mediatore_id, nome: mediazione.mediatore_nome, cognome: mediazione.mediatore_cognome,
      interno: false, lingue: [], attivo: true, utente_id: null, usa_gestionale: mediazione.mediatore_usa_gestionale,
    })
  }
  // Dal calendario, il mediatore della riga in cui si e' fatto clic.
  const proposto = iniziali?.mediatore
  if (!mediazione && proposto && !elenco.some((m) => m.id === proposto.id)) {
    elenco.unshift({ ...proposto, interno: false, lingue: [], attivo: true, utente_id: null })
  }
  // Con la lingua, due gruppi: chi la parla e chi non ha lingue nell'albo; senza lingua, con la sede, chi
  // lavora in quella sede (o in tutte) e gli altri. Dentro ogni gruppo, in cima chi e' della sede.
  const nomeLingua = lingue.find((l) => l.codice === lingua)?.nome ?? lingua
  const gruppo = (m: Mediatore) => lingua
    ? (m.parla_lingua === false ? 'Lingue non indicate nell’albo' : `Parlano ${nomeLingua.toLowerCase()}`)
    : !sede ? undefined : m.della_sede ? 'Lavorano in questa sede' : 'Gli altri mediatori'
  elenco.sort((a, b) => Number(b.parla_lingua !== false) - Number(a.parla_lingua !== false)
    || Number(!!b.della_sede) - Number(!!a.della_sede))
  const scelto = elenco.find((m) => m.id === mediatoreId) ?? null
  const durataPersonalizzata = !DURATE.some((d) => d.valore === durata)
  const durate = durataPersonalizzata ? [{ valore: durata, etichetta: `${durata} minuti` }, ...DURATE] : DURATE
  // Il percorso e' obbligatorio per le mediazioni nuove; quelle programmate prima della scelta
  // del percorso si spostano anche senza.
  const quante = Number(volte)
  const serieValida = !frequenza || (Number.isInteger(quante) && quante >= 2 && quante <= 26)
  const pronto = !!mediatoreId && !!data && /^\d{2}:\d{2}$/.test(ora) && (!!percorsoId || !!mediazione) && serieValida

  async function salva(date = conferme) {
    setErrore(null); setInCorso(true)
    const corpo: Record<string, unknown> = {
      mediatore_id: mediatoreId, data, ora, durata_minuti: Number(durata), lingua: lingua || null,
      sede_id: sede || null, luogo: luogo.trim() || null, conferma_sovrapposizione: date.sovrapposizione,
      conferma_fuori_orario: date.fuori_orario, conferma_non_disponibile: date.non_disponibile,
    }
    if (!mediazione || (stanza || null) !== mediazione.risorsa_id) corpo.risorsa_id = stanza || null
    // La nota si conserva cifrata e l'API la riscrive ogni volta che arriva: spostando, si manda
    // solo se e' cambiata.
    if (!mediazione || nota !== (mediazione.nota ?? '')) corpo.nota = nota.trim() || null
    if (percorsoId && percorsoId !== mediazione?.percorso_id) corpo.percorso_id = percorsoId
    if (!mediazione || (ente?.id ?? null) !== mediazione.ente_id) corpo.ente_id = ente?.id ?? null
    if (!mediazione && frequenza) corpo.ripetizione = { frequenza, volte: quante }
    try {
      const chi = scelto ? `${scelto.nome} ${scelto.cognome}` : 'Il mediatore'
      if (mediazione) {
        const r = await api.patch<EsitoEmail & { modificata: boolean; campi?: string[] }>(`/mediazioni/${mediazione.id}`, corpo)
        if (!r.modificata) fatto('Nessuna modifica da salvare.')
        else if (r.campi?.every((c) => c === 'percorso_id')) fatto('Fatto: le ore andranno sul percorso scelto.')
        else if (!scelto?.usa_gestionale) fatto('Fatto. Il mediatore non usa il gestionale: avvisalo tu delle novità.')
        else {
          fatto([r.email_inviata ? `Fatto. ${chi} riceve un avviso con le novità, anche per email.`
            : `Fatto. ${chi} riceve un avviso con le novità nel gestionale.`, fraseEmail(r, true)].filter(Boolean).join(' '))
        }
      } else {
        const r = await api.post<EsitoEmail & { avvisato: boolean; recapito_mediatore: string | null; ids: string[] }>(
          `/persone/${personaId}/mediazioni`, corpo)
        // Il recapito del mediatore (532), se c'e': dove riceve, da dire alla persona.
        const recapito = r.recapito_mediatore ? `Recapito del mediatore: ${r.recapito_mediatore}.` : ''
        const quali = r.ids.length > 1 ? `${r.ids.length} mediazioni programmate` : 'Mediazione programmata'
        fatto([r.avvisato
          ? [`${quali}. ${chi} riceve un avviso${r.email_inviata ? ' nel gestionale e per email' : ''}`
            + `${r.ids.length > 1 ? ', uno solo per tutte,' : ''} e le trova fra le sue.`, fraseEmail(r, true)].filter(Boolean).join(' ')
          : `${quali}. Il mediatore non usa il gestionale: ricordati di avvisarlo.`, recapito]
          .filter(Boolean).join(' '))
      }
    } catch (e) {
      if (e instanceof ErroreApi
          && (e.codice === 'SOVRAPPOSTA' || e.codice === 'FUORI_ORARIO' || e.codice === 'MEDIATORE_NON_DISPONIBILE')) {
        setAvviso({ codice: e.codice, messaggio: e.message })
      } else setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale larga titolo={mediazione ? 'Sposta o modifica la mediazione' : 'Programma una mediazione'} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={!pronto}
                       onClick={() => { const nessuna = { sovrapposizione: false, fuori_orario: false, non_disponibile: false }
                         setConferme(nessuna); setAvviso(null); void salva(nessuna) }}>
                {mediazione ? 'Salva le modifiche' : 'Programma'}
              </Bottone>
            </>}>
      {intestazione}
      <h3 className="rete-titoletto">Chi fa la mediazione</h3>
      <div className="griglia-modulo">
        <Scelta etichetta="Lingua" valore={lingua} vuota="— qualsiasi —" cambia={(v) => setLingua(codiceDaVoce(v))}
                opzioni={opzioniConAltreVoci(lingue)}
                aiuto="Scegliendo la lingua vedi prima i mediatori che la parlano, poi quelli che non hanno lingue nell’albo." />
        <Campo tipo="search" etichetta="Cerca per nome" valore={cerca} cambia={setCerca} maxLength={120}
               segnaposto="Cognome o nome" />
        <Scelta etichetta="Mediatore" obbligatorio valore={mediatoreId} cambia={setMediatoreId}
                vuota={trovati === null ? 'Caricamento…' : elenco.length ? '— scegli —' : 'Nessun mediatore trovato'}
                // Con il comune in cui lavora (532): «Rossi Anna — Perugia».
                opzioni={elenco.map((m) => ({
                  valore: m.id, gruppo: gruppo(m),
                  etichetta: `${m.cognome} ${m.nome}${m.comune_lavoro_nome ? ` — ${m.comune_lavoro_nome}` : ''}`,
                }))}
                aiuto={!scelto ? (sede ? 'In cima chi lavora nella sede scelta più sotto, o in tutte le sedi.' : undefined)
                  : scelto.usa_gestionale
                  ? 'Riceve un avviso nel gestionale e un’email, e trova la mediazione fra le sue.'
                  : 'Non usa il gestionale: dopo averla programmata, avvisalo tu.'} />
      </div>

      <h3 className="rete-titoletto">Quando</h3>
      <div className="griglia-modulo">
        <Campo tipo="date" etichetta="Giorno" obbligatorio valore={data} min={giornoDiOggi()} cambia={setData} />
        <Campo tipo="time" etichetta="Ora d’inizio" obbligatorio valore={ora} cambia={setOra} passi={300} />
        <Scelta etichetta="Durata prevista" valore={durata} opzioni={durate} cambia={setDurata} />
      </div>
      {!mediazione && (
        <div className="griglia-modulo">
          <Scelta etichetta="Si ripete" valore={frequenza} cambia={setFrequenza} opzioni={FREQUENZE}
                  aiuto="Una mediazione per ogni data, alla stessa ora: ognuna si sposta e si annulla da sola." />
          {frequenza && (
            <Campo tipo="number" etichetta="Quante in tutto" obbligatorio valore={volte} cambia={setVolte} min={2} max={26}
                   errore={serieValida ? undefined : 'Da 2 a 26, la prima compresa.'}
                   aiuto="Al più 26. Il mediatore riceve un avviso solo, con il file di tutte le date." />
          )}
        </div>
      )}

      <h3 className="rete-titoletto">Dove</h3>
      <div className="griglia-modulo">
        <Scelta etichetta="Sede" valore={sede} vuota="— nessuna sede CIDIS —" cambia={(v) => { setSede(v); setStanza('') }}
                opzioni={opzioniSedi(sedi)} />
        <SceltaStanza sedeId={sede || null} valore={stanza} cambia={setStanza} colloqui />
        <Campo etichetta="Luogo preciso" valore={luogo} cambia={setLuogo} maxLength={300}
               aiuto="Facoltativo. Per esempio: Questura, ufficio immigrazione. Lo legge il mediatore nel gestionale; non va negli avvisi né nelle email." />
      </div>
      <AreaTesto etichetta="Nota per il mediatore" valore={nota} cambia={setNota} maxLength={2000}
                 aiuto="Facoltativa. La leggono il mediatore e chi vede questa scheda." />

      <h3 className="rete-titoletto">Per quale ente</h3>
      <SceltaEnte etichetta="Ente" vuota="— nessuno —" valore={ente} cambia={setEnte} perimetroPersone />
      <p className="piccolo tenue">
        Facoltativo: l’ente che ha chiesto la mediazione. Lo vede solo chi segue la scheda; non va al mediatore.
      </p>

      <h3 className="rete-titoletto">Dove finiscono le ore</h3>
      <SceltaPercorso personaId={personaId} dati={dati} valore={percorsoId} cambia={setPercorsoId}
                      percorsoAperto={percorsoAperto} />

      {/* L'esito sta subito sopra i comandi: con il modulo lungo deve comparire dove si e' premuto. */}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {avviso && (
        <Avviso tipo="attenzione">
          {avviso.messaggio}
          <div className="riga">
            <Bottone variante="secondario" dimensione="piccolo" inCorso={inCorso}
                     onClick={() => {
                       const date = { ...conferme,
                         ...(avviso.codice === 'SOVRAPPOSTA' ? { sovrapposizione: true }
                           : avviso.codice === 'FUORI_ORARIO' ? { fuori_orario: true } : { non_disponibile: true }) }
                       setConferme(date); setAvviso(null); void salva(date)
                     }}>
              Programma lo stesso
            </Bottone>
            <Bottone variante="discreto" dimensione="piccolo" onClick={() => setAvviso(null)}>
              Scelgo un altro orario
            </Bottone>
          </div>
        </Avviso>
      )}
    </Modale>
  )
}

/**
 * Annulla una mediazione. In una serie (557) c'e' «Annulla anche le successive» (decisione di Davide del
 * 3/11/2026): con questa e quelle in programma dopo, un avviso e un'email soli al mediatore.
 */
function AnnullaMediazione({ mediazione, chiudi, fatto }: {
  mediazione: MediazioneScheda; chiudi: () => void; fatto: (r: EsitoEmail & { annullate: number }) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [successive, setSuccessive] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const inSerie = !!mediazione.serie_id && mediazione.serie_quante > 1

  async function annulla() {
    setErrore(null); setInCorso(true)
    try {
      fatto(await api.post<EsitoEmail & { annullate: number }>(`/mediazioni/${mediazione.id}/annulla`,
        { motivo: motivo.trim(), ...(inSerie ? { anche_successive: successive } : {}) }))
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo="Annulla la mediazione" chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Non annullare</Bottone>
              <Bottone variante="critico" inCorso={inCorso} disabled={motivo.trim().length < 3}
                       onClick={() => void annulla()}>{successive ? 'Annulla queste mediazioni' : 'Annulla la mediazione'}</Bottone>
            </>}>
      <p>
        {giornoVicino(mediazione.data)} alle {mediazione.ora} con {nomeMediatore(mediazione)}.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <AreaTesto etichetta="Perché si annulla" obbligatorio valore={motivo} cambia={setMotivo} maxLength={500}
                 aiuto="Resta nella scheda della persona." />
      {inSerie && (
        <Spunta etichetta="Annulla anche le successive" valore={successive} cambia={setSuccessive}
                aiuto={`È una di ${mediazione.serie_quante} mediazioni ripetute: si annullano anche quelle in programma dopo `
                  + 'questa, con lo stesso motivo. Il mediatore riceve un avviso solo.'} />
      )}
    </Modale>
  )
}

/**
 * «Registra l'intervento» di una mediazione svolta che non l'ha avuto: per esempio perché alla
 * svolta mancavano i consensi o il percorso era stato chiuso. Il percorso si può cambiare.
 */
function RegistraInterventoMediazione({ personaId, dati, mediazione, percorsoAperto, chiudi, fatto }: {
  personaId: string
  dati: MediazioniPersona
  mediazione: MediazioneScheda
  percorsoAperto: () => Promise<void>
  chiudi: () => void
  fatto: () => void
}) {
  const [percorsoId, setPercorsoId] = useState(() => percorsoIniziale(dati, mediazione.percorso_id))
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function registra() {
    setErrore(null); setInCorso(true)
    try {
      await api.post(`/mediazioni/${mediazione.id}/intervento`, { percorso_id: percorsoId })
      fatto()
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine. Riprova fra poco.')
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo="Registra l’intervento della mediazione" chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={!percorsoId} onClick={() => void registra()}>
                Registra l’intervento
              </Bottone>
            </>}>
      <p>
        {giornoVicino(mediazione.data)} alle {mediazione.ora} con {nomeMediatore(mediazione)}
        {mediazione.ore_svolte != null && `: ${durataLeggibile(mediazione.ore_svolte * 60)} di mediazione`}.
        L’intervento nasce in bozza sul percorso scelto, a tuo nome: poi lo completi e lo salvi dalla linguetta Percorsi.
      </p>
      <SceltaPercorso personaId={personaId} dati={dati} valore={percorsoId} cambia={setPercorsoId}
                      percorsoAperto={percorsoAperto} />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
